import type React from 'react'
import { useLayoutEffect, useState } from 'react'
import type { QuoteFile } from '../types'
import { usePdfRender } from '../providers/PdfRenderProvider'
import { retryCorsImageOnError } from './corsImage'
import { generateSrcSet } from './generateSrcSet'
import { getPrintImageWidth } from './getPrintImageWidth'

/**
 * In a PDF render, the S3 resize variant that fits the slot `ref`'s image sits in (#25493).
 *
 * The slot is measured from the image's parent before the browser paints, so the full-size
 * source never starts loading. Callers render their `<img>` without a source while `pending`;
 * `usePdfReady` ignores an `<img>` with no `src`, so this does not mark the PDF ready early.
 *
 * `src` is null outside a PDF render, or when the slot or the variant URL is unknown — the caller
 * then renders as it always has.
 *
 * `onError` is for the `<img>` that shows `src`: the resize task is best-effort, so a missing
 * variant falls back once to the original; a second failure gets the usual CORS retry.
 */
export function usePrintSizedSrc(
  file: QuoteFile | undefined,
  ref: React.RefObject<HTMLElement>
): { pending: boolean; src: string | null; onError: React.ReactEventHandler<HTMLImageElement> } {
  const isPdf = usePdfRender()
  // undefined = not measured yet
  const [src, setSrc] = useState<string | null | undefined>()

  useLayoutEffect(() => {
    if (!isPdf) {
      return
    }
    const slotWidth = ref.current?.parentElement?.clientWidth
    const size = getPrintImageWidth(slotWidth ? slotWidth * (window.devicePixelRatio || 1) : undefined)
    // generateSrcSet only reads `src` and `s3PublicKey`, hence the narrow deps
    setSrc(size ? generateSrcSet({ file, size }) : null)
  }, [isPdf, file?.src, file?.s3PublicKey])

  const onError: React.ReactEventHandler<HTMLImageElement> = (e) => {
    const img = e.currentTarget
    // A flag, not a URL comparison: `img.src` is resolved, `file.src` may not be
    if (src && file?.src && img.dataset.printFallback !== 'true') {
      img.dataset.printFallback = 'true'
      img.src = file.src
      return
    }
    retryCorsImageOnError(e)
  }

  return { pending: isPdf && src === undefined, src: isPdf ? src ?? null : null, onError }
}
